import { ObjectbuildingBuilding, CondominiumCondominium, Contactcontactv2Company, InsuranceInsurance, MortgageMortgage, ObjectobjectObject, ObjectdeviceDevice, Propertyv2Property, ObjectroomRoom, ObjecttenancyTenancy, TicketTicketReference, V2Person, V2Request } from '@/types/api'
import {
  buildDeviceDetailQuery,
  buildDeviceFiltersFromConnection,
  getDeviceAssigneePropKey
} from '@/misc/utils/deviceNavigation'
import { isActiveTenancyStatus } from '@/misc/utils/tenancyUtils'

const ticketConnections = {
  methods: {
    setInsuranceId(insurance: InsuranceInsurance, middleClick: boolean = false): void {
      const source = insurance.referenceId.startsWith('building') ? 'building' : 'property'
      if (source === 'building') {
        localStorage.setItem('expandBuildingInsuranceId', insurance.id)
      } else if (source === 'property') {
        localStorage.setItem('expandPropertyInsuranceId', insurance.id)
      }
      this.redirectToParentDetail(insurance, middleClick)
    },
    setMortgageId(mortgage: MortgageMortgage, middleClick: boolean = false): void {
      if (mortgage.referenceId.startsWith('prop')) {
        localStorage.setItem('expandPropertyMortgageId', mortgage.id)
      }
      this.redirectToParentDetail(mortgage, middleClick)
    },
    redirectToParentDetail(item: InsuranceInsurance | MortgageMortgage, middleClick: boolean): void {
      if (item.referenceId.startsWith('building')) {
        if (!middleClick) {
          this.$router.push(this.redirectToDetailPage(item, 'building'))
        } else {
          const route = this.$router.resolve(this.redirectToDetailPage(item, 'building'))
          window.open(route.href, '_blank')
        }
      } else if (item.referenceId.startsWith('prop')) {
        if (!middleClick) {
          this.$router.push(this.redirectToDetailPage(item, 'prop'))
        } else {
          const route = this.$router.resolve(this.redirectToDetailPage(item, 'prop'))
          window.open(route.href, '_blank')
        }
      }
    },
    getReferenceName(id: string): string {
      let name = ''
      if (id.startsWith('device')) {
        const device = this.getConnection(id)
        if (device.referenceId.startsWith('prop')) {
          this.allConnections.properties.forEach(property => {
            if (property.id === device.referenceId) {
              name = property.name
            }
          })
        } else if (device.referenceId.startsWith('building')) {
          this.allConnections.buildings.forEach(building => {
            if (building.id === device.referenceId) {
              name = building.name
            }
          })
        } else if (device.referenceId.startsWith('obj')) {
          this.allConnections.objects.forEach(object => {
            if (object.id === device.referenceId) {
              name = object.name
            }
          })
        }
      }
      if (id.startsWith('mortgage') || id.startsWith('ins')) {
        const item = this.getConnection(id)
        if (item.referenceId.startsWith('prop')) {
          this.allConnections.properties.forEach(property => {
            if (property.id === item.referenceId) {
              name = property.name
            }
          })
        } else if (item.referenceId.startsWith('building')) {
          this.allConnections.buildings.forEach(building => {
            if (building.id === item.referenceId) {
              name = building.name
            }
          })
        }
      }
      return name
    },
    findConnectionId(reference: TicketTicketReference | { relatedIds?: string[] } | null | undefined, type: string): string {
      if (!reference?.relatedIds?.length || !type) {
        return ''
      }
      return reference.relatedIds.find((x: string) => x?.startsWith(type)) || ''
    },
    findObjectConnectionId(reference: TicketTicketReference | { id?: string, relatedIds?: string[] } | null | undefined): string | undefined {
      if (!reference) {
        return undefined
      }
      const fromRelated = reference.relatedIds?.find((id: string) => id?.startsWith('obj'))
      if (fromRelated) {
        return fromRelated
      }
      if (reference.id?.startsWith('device')) {
        const device = this.getConnection(reference.id)
        if (device?.referenceId?.startsWith('obj')) {
          return device.referenceId
        }
      }
      return undefined
    },
    getDeviceRoomId(reference: TicketTicketReference | { id?: string } | null | undefined): string | undefined {
      if (!reference?.id?.startsWith('device')) {
        return undefined
      }
      const device = this.getConnection(reference.id)
      return device?.roomId || undefined
    },
    getDeviceRoom(reference: TicketTicketReference | { id?: string } | null | undefined): ObjectroomRoom | undefined {
      const roomId = this.getDeviceRoomId(reference)
      if (!roomId) {
        return undefined
      }
      return this.getConnection(roomId) || undefined
    },
    setRoomId(source: string, id: string): void {
      if (source === 'building') {
        localStorage.setItem('expandBuildingRoomId', id)
      } else if (source === 'object') {
        localStorage.setItem('expandObjectRoomId', id)
      }
    },
    /**
     * Open a room by expanding it on the building/object detail page.
     * Optional context.buildingId is used when allConnections is unavailable (e.g. TicketDetailsView).
     */
    navigateToRoom(
      room: ObjectroomRoom | null | undefined,
      roomRefId?: string,
      newTab: boolean = false,
      context?: { buildingId?: string, objectId?: string }
    ): void {
      if (!room?.referenceId) {
        return
      }
      const id = roomRefId || room.id
      if (!id) {
        return
      }

      let route: Record<string, unknown> | string = ''
      if (room.referenceId.startsWith('building')) {
        // Pass building id as item.id — list-view overrides of redirectToDetailPage use item.id directly
        route = this.redirectToDetailPage({ id: room.referenceId }, 'building')
      } else if (room.referenceId.startsWith('obj')) {
        const objectFromConnections = this.allConnections?.objects?.find(
          (x: ObjectobjectObject) => x.id === room.referenceId
        )
        const buildingId = context?.buildingId ||
          objectFromConnections?.buildingId ||
          this.allConnections?.buildings?.find(
            (y: ObjectbuildingBuilding) => y.objectIds?.includes(room.referenceId)
          )?.id
        if (!buildingId) {
          return
        }
        const objectId = context?.objectId || room.referenceId
        route = this.redirectToDetailPage(
          { id: objectId, buildingId },
          'obj'
        )
      }
      if (!route || (typeof route === 'object' && route.params && !(route.params as { id?: string }).id)) {
        return
      }

      const source = room.referenceId.startsWith('building') ? 'building' : 'object'
      this.setRoomId(source, id)

      if (newTab) {
        const resolved = this.$router.resolve(route)
        window.open(resolved.href, '_blank')
      } else {
        this.$router.push(route)
      }
    },
    getObjectConnectionLabel(object: ObjectobjectObject | null | undefined): string {
      if (!object) {
        return ''
      }
      const name = object.name || ''
      const number = object.number || ''
      if (name) {
        return number ? `${name} - ${number}` : name
      }
      return number || '-'
    },
    getObjectLocationNumber(object: ObjectobjectObject | null | undefined): string {
      return object?.number || ''
    },
    getConnection(id?: string | null) {
      if (!id || typeof id !== 'string') {
        return undefined
      }
      if (id.startsWith('condominium')) {
        return this.allConnections.condominiums?.find((x: CondominiumCondominium) => x.id === id)
      } else if (id.startsWith('tenancy')) {
        return this.allConnections.tenancies?.find((x: ObjecttenancyTenancy) => x.id === id)
      } else if (id.startsWith('obj')) {
        return this.allConnections.objects?.find((x: ObjectobjectObject) => x.id === id)
      } else if (id.startsWith('building')) {
        return this.allConnections.buildings?.find((x: ObjectbuildingBuilding) => x.id === id)
      } else if (id.startsWith('prop')) {
        return this.allConnections.properties?.find((x: Propertyv2Property) => x.id === id)
      } else if (id.startsWith('contact')) {
        return this.allConnections.contacts?.find((x: V2Person | Contactcontactv2Company) => x.id === id)
      } else if (id.startsWith('device')) {
        return this.allConnections.devices?.find((x: ObjectdeviceDevice) => x.id === id)
      } else if (id.startsWith('request')) {
        return this.allConnections.requests?.find((x: V2Request) => x.id === id)
      } else if (id.startsWith('room')) {
        return this.allConnections.rooms?.find((x: ObjectroomRoom) => x.id === id) || null
      } else if (id.startsWith('mortgage')) {
        return this.allConnections.mortgages?.find((x: MortgageMortgage) => x.id === id) || null
      } else if (id.startsWith('ins')) {
        return this.allConnections.insurances?.find((x: InsuranceInsurance) => x.id === id) || null
      }
    },
    getContactName(contactId: string): string | undefined {
      return this.listContacts.find((x: V2Person | Contactcontactv2Company) => x.id === contactId)?.name
    },
    redirectToDetailPage(item: any, type: string) {
      if (type === 'contact') {
        const contactId = item.id
        if (contactId !== null) {
          if (this.isOwnerModule) {
            return {
              name: 'OwnerContactDetailsView',
              params: { id: contactId }
            }
          } else if (this.isAssetModule) {
            return {
              name: 'AssetContactDetailsView',
              params: { id: contactId }
            }
          } else if (this.isAccountingModule) {
            return {
              name: 'AccountingContactDetailsView',
              params: { id: contactId }
            }
          } else if (this.isMarketingModule) {
            return {
              name: 'MarketingContactDetailsView',
              params: { id: contactId }
            }
          } else {
            return {
              name: 'AdminContactDetailsView',
              params: { id: contactId }
            }
          }
        }
      } else if (type === 'obj') {
        const buildingId = item.buildingId || this.allConnections.buildings?.find((y: ObjectbuildingBuilding) => y.objectIds?.includes(item.id))?.id
        if (!buildingId) {
          return ''
        }
        const objectId = item?.id
        const view = 'objects'
        if (this.isOwnerModule) {
          return {
            name: 'OwnerBuildingDetailsView',
            params: { id: buildingId },
            query: {
              view: view,
              elementSelected: objectId
            }
          }
        } else if (this.isAdminModule) {
          return {
            name: 'AdminBuildingDetailsView',
            params: { id: buildingId },
            query: {
              view: view,
              elementSelected: objectId
            }
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetBuildingDetailsView',
            params: { id: buildingId },
            query: {
              view: view,
              elementSelected: objectId
            }
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingBuildingDetailsView',
            params: { id: buildingId },
            query: {
              view: view,
              elementSelected: objectId
            }
          }
        }
      } else if (type === 'building') {
        const buildingId = item.id.startsWith('building') ? item.id : item.id.startsWith('room') || item.id.startsWith('mortgage') ? item.referenceId : null
        if (buildingId) {
          if (this.isOwnerModule) {
            return {
              name: 'OwnerBuildingDetailsView',
              params: { id: buildingId },
              query: { view: 'building' }
            }
          } else if (this.isAssetModule) {
            return {
              name: 'AssetBuildingDetailsView',
              params: { id: buildingId },
              query: { view: 'building' }
            }
          } else if (this.isAccountingModule) {
            return {
              name: 'AccountingBuildingDetailsView',
              params: { id: buildingId },
              query: { view: 'building' }
            }
          } else if (this.isAdminModule) {
            return {
              name: 'AdminBuildingDetailsView',
              params: { id: buildingId },
              query: { view: 'building' }
            }
          }
        }
      } else if (type === 'prop') {
        const propertyId = item.id.startsWith('prop') ? item.id : item.id.startsWith('mortgage') || item.id.startsWith('ins') ? item.referenceId : null
        if (this.isOwnerModule) {
          return { name: 'OwnerPropertyDetailsView', params: { id: propertyId } }
        } else if (this.isAdminModule) {
          return { name: 'AdminPropertyDetailsView', params: { id: propertyId } }
        } else if (this.isAssetModule) {
          return { name: 'AssetPropertyDetailsView', params: { id: propertyId } }
        } else if (this.isAccountingModule) {
          return { name: 'AccountingPropertyDetailsView', params: { id: propertyId } }
        }
      }
      return ''
    },
    buildDeviceFiltersForConnection(device: ObjectdeviceDevice, extras: {
      object?: ObjectobjectObject | null
      room?: ObjectroomRoom | null
      tenantContactIds?: string[]
    } = {}) {
      const object = extras.object || (
        device.referenceId?.startsWith('obj')
          ? this.allConnections?.objects?.find((x: ObjectobjectObject) => x.id === device.referenceId)
          : undefined
      )
      const room = extras.room || (
        device.roomId
          ? this.allConnections?.rooms?.find((x: ObjectroomRoom) => x.id === device.roomId)
          : undefined
      )
      const tenancy = object && this.allConnections?.tenancies
        ? this.allConnections.tenancies.find((x: ObjecttenancyTenancy) =>
          x.objectId === object.id && isActiveTenancyStatus(x.status)
        )
        : undefined
      const assigneeProp = getDeviceAssigneePropKey({
        isAccountingModule: this.isAccountingModule,
        isAdminModule: this.isAdminModule,
        isAssetModule: this.isAssetModule
      })
      const deviceTypeLabel = device.type && device.type !== 'DEVICE_TYPE_UNDEFINED'
        ? String(this.$t('message.onBoarding.devices.typeValues.' + device.type))
        : ''
      return buildDeviceFiltersFromConnection({
        device,
        object,
        room,
        deviceTypeLabel,
        installedLabel: String(this.$t('message.onBoarding.devices.filter.installed')),
        uninstalledLabel: String(this.$t('message.onBoarding.devices.filter.uninstalled')),
        tenantContactIds: extras.tenantContactIds || tenancy?.tenant?.contactIds || [],
        assigneeContactId: object?.contacts?.[assigneeProp] || ''
      })
    },
    buildDeviceFiltersFromTicketReference(refItem: {
      device?: ObjectdeviceDevice | null
      object?: ObjectobjectObject | null
      tenancy?: ObjecttenancyTenancy | null
      relatedReference?: Array<{ id?: string, name?: string } | null>
      listContact?: Array<{ id?: string } | null>
    } | null | undefined) {
      if (!refItem?.device) {
        return buildDeviceFiltersFromConnection({})
      }
      const room = (refItem.relatedReference || []).find(item =>
        item && item.id && item.id === refItem.device?.roomId
      ) as ObjectroomRoom | undefined
      const tenantContactIds = refItem.tenancy?.tenant?.contactIds ||
        (refItem.listContact || []).map(c => c?.id).filter(Boolean) as string[]
      return this.buildDeviceFiltersForConnection(refItem.device, {
        object: refItem.object,
        room,
        tenantContactIds
      })
    },
    redirectToDeviceDetail(device: ObjectdeviceDevice) {
      if (!device?.referenceId || !device?.id) {
        return ''
      }
      const referenceId = device.referenceId
      const deviceId = device.id
      const query = buildDeviceDetailQuery(deviceId, this.buildDeviceFiltersForConnection(device))
      if (referenceId.startsWith('obj') || referenceId.startsWith('building')) {
        let id = null
        if (referenceId.startsWith('building')) {
          id = referenceId
        } else {
          const item = this.allConnections.buildings.filter((x: ObjectbuildingBuilding) => x?.objectIds?.includes(referenceId))
          if (item.length > 0) {
            id = item[0].id
          }
        }
        if (id && deviceId) {
          if (this.isOwnerModule) {
            return { name: 'OwnerBuildingDetailsView', params: { id: id }, query }
          } else if (this.isAdminModule) {
            return { name: 'AdminBuildingDetailsView', params: { id: id }, query }
          } else if (this.isAssetModule) {
            return { name: 'AssetBuildingDetailsView', params: { id: id }, query }
          } else if (this.isAccountingModule) {
            return { name: 'AccountingBuildingDetailsView', params: { id: id }, query }
          }
        }
      } else {
        if (this.isOwnerModule) {
          return { name: 'OwnerPropertyDetailsView', params: { id: referenceId }, query }
        } else if (this.isAdminModule) {
          return { name: 'AdminPropertyDetailsView', params: { id: referenceId }, query }
        } else if (this.isAssetModule) {
          return { name: 'AssetPropertyDetailsView', params: { id: referenceId }, query }
        } else if (this.isAccountingModule) {
          return { name: 'AccountingPropertyDetailsView', params: { id: referenceId }, query }
        }
      }
      return ''
    },
    redirectToRequestDetail(objectId: string, requestId: string) {
      return {
        name: 'MarketingObjectDetailsView',
        params: { id: objectId },
        query: {
          view: 0,
          elementSelected: requestId
        }
      }
    }
  }
}

export { ticketConnections }
